import { useEffect, useState } from "react";
import { submitLicensing } from "../../lib/api";
import SEO from "../../components/SEO";

const TIERS = [
  {
    id: "Tier 1 – Limited Awareness",
    name: "Limited Awareness License",
    pill: "Tier 1 · Non-Commercial",
    fee: "No Fee — written approval required",
    desc: "Free use for mission-aligned awareness efforts. Designed to encourage organic, mission-aligned usage.",
    permitted: [
      "Organic social posts (IG, FB, LinkedIn, TikTok, YouTube organic)",
      "Live awareness events (non-ticketed)",
      "Internal presentations",
      "Educational settings",
      "Community awareness videos",
    ],
    restrictions: [
      "No paid advertising or boosted posts",
      "No political fundraising ads",
      "No broadcast or OTT use",
      "No national paid campaigns",
      "No sublicensing · No lyric alteration · No derivative works",
      "No monetized use tied to ad revenue",
    ],
  },
  {
    id: "Tier 2 – Campaign Sync",
    name: "Campaign Sync License",
    pill: "Tier 2 · Paid / Broadcast",
    fee: "Sync fee or negotiated consideration",
    desc: "Required for paid campaigns, film, broadcast, and national initiatives.",
    permitted: [
      "Paid advertising · Fundraising campaigns",
      "Political advocacy campaigns",
      "Broadcast television · Streaming platform ads",
      "Feature film or documentary",
      "National brand partnerships",
      "Exclusive campaign alignment",
    ],
    restrictions: [
      "Defined term · Defined territory · Defined media",
      "Master + publishing clearance required",
      "Optional exclusivity by category or duration",
    ],
  },
  {
    id: "Tier 3 – Custom Derivative",
    name: "Custom Derivative & Co-Branded Works",
    pill: "Tier 3 · Bespoke",
    fee: "Per-agreement",
    desc: "For co-branded campaigns, custom edits, or derivative works. Ownership structure defined per agreement.",
    permitted: [
      "Lyric adaptation",
      "Custom brand integration",
      "Campaign-specific edits",
      "Alternate production versions",
    ],
    restrictions: [
      "Original composition ownership remains protected unless otherwise agreed in writing.",
    ],
  },
];

export default function Licensing() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({
    organization: "", contact_name: "", email: "", phone: "",
    tier: "Tier 1 – Limited Awareness", track_title: "", intended_use: "",
    platforms: "", paid_or_organic: "Organic", duration: "", notes: "",
  });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setStatus(null);
    try {
      await submitLicensing(form);
      setStatus("Thank you — your request has been received. We'll respond within 3–5 business days.");
      setForm({ organization: "", contact_name: "", email: "", phone: "", tier: "Tier 1 – Limited Awareness", track_title: "", intended_use: "", platforms: "", paid_or_organic: "Organic", duration: "", notes: "" });
    } catch {
      setStatus("Submission failed. Email fostercareawareness365@gmail.com directly.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="anthem-licensing-page">
      <SEO
        path="/anthem/licensing"
        title="Music Licensing | Foster Care Anthem · 3 Tiers"
        description="License Foster Care Anthem VOL I for mission-aligned campaigns. Three tiers: Limited Awareness (free, organic), Campaign Sync (paid/broadcast), and Custom Derivative (bespoke). 3–5 business day response."
      />
      <section className="border-b border-an-border">
        <div className="container-xl pt-24 pb-16">
          <div className="overline text-an-gold mb-5">Licensing</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">Permission <br/><span className="italic">to act.</span></h1>
          <p className="mt-8 text-an-muted text-lg max-w-3xl leading-relaxed">
            Foster Care Anthem VOL 1 is available for mission-aligned organizations under a Limited Awareness License — supporting foster care awareness, adoption advocacy, anti-trafficking efforts, and child welfare initiatives.
          </p>
        </div>
      </section>

      <section id="licensing-tiers-section" className="border-b border-an-border">
        <div className="container-xl py-20">
          <h2 className="font-display text-3xl md:text-4xl mb-10">Three Tiers. One Standard.</h2>
          <div className="grid md:grid-cols-3 gap-0 border border-an-border">
            {TIERS.map((t, i) => (
              <div key={t.id} className={`p-8 ${i<2 ? "md:border-r" : ""} border-an-border`}>
                <div className="overline text-an-gold mb-3">{t.pill}</div>
                <h3 className="font-display text-2xl mb-2">{t.name}</h3>
                <div className="font-mono text-sm text-an-muted mb-4">Fee: {t.fee}</div>
                <p className="text-sm text-an-muted leading-relaxed mb-5">{t.desc}</p>
                <div className="overline text-an-muted mb-2">Permitted</div>
                <ul className="text-sm space-y-1 mb-5">
                  {t.permitted.map((p) => <li key={p}>· {p}</li>)}
                </ul>
                <div className="overline text-an-muted mb-2">Restrictions</div>
                <ul className="text-sm space-y-1 text-an-muted">
                  {t.restrictions.map((p) => <li key={p}>· {p}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-an-muted">
            <strong className="text-an-ink">Credit Requirement:</strong> Music: "Foster Care Anthem VOL 1 – [Song Title]"
          </p>
        </div>
      </section>

      <section>
        <div className="container-xl py-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-an-gold mb-4">Request Approval</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">Submit a Licensing Inquiry</h2>
            <p className="mt-6 text-an-muted leading-relaxed">
              Written confirmation is required prior to use for all tiers. Tell us about your organization, intended use, and platforms — we'll get back within 3–5 business days.
            </p>
            <div className="mt-8 pt-8 border-t border-an-border">
              <div className="overline text-an-muted mb-2">Or Email Directly</div>
              <a href="mailto:fostercareawareness365@gmail.com" className="font-mono link-ul" data-testid="licensing-email">fostercareawareness365@gmail.com</a>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-7 space-y-6" data-testid="licensing-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="fc-label block mb-2">Organization</label>
                <input required value={form.organization} onChange={update("organization")} className="fc-input" data-testid="licensing-org" />
              </div>
              <div>
                <label className="fc-label block mb-2">Contact Name</label>
                <input required value={form.contact_name} onChange={update("contact_name")} className="fc-input" data-testid="licensing-contact" />
              </div>
              <div>
                <label className="fc-label block mb-2">Email</label>
                <input required type="email" value={form.email} onChange={update("email")} className="fc-input" data-testid="licensing-email-input" />
              </div>
              <div>
                <label className="fc-label block mb-2">Phone (optional)</label>
                <input value={form.phone} onChange={update("phone")} className="fc-input" data-testid="licensing-phone" />
              </div>
              <div>
                <label className="fc-label block mb-2">Requested Tier</label>
                <select value={form.tier} onChange={update("tier")} className="fc-input bg-transparent" data-testid="licensing-tier">
                  {TIERS.map((t) => <option key={t.id} value={t.id} className="bg-an-bg">{t.id}</option>)}
                </select>
              </div>
              <div>
                <label className="fc-label block mb-2">Specific Track (optional)</label>
                <input value={form.track_title} onChange={update("track_title")} className="fc-input" placeholder="e.g. Philly Foster Kid" data-testid="licensing-track" />
              </div>
            </div>
            <div>
              <label className="fc-label block mb-2">Intended Use</label>
              <textarea required rows={3} value={form.intended_use} onChange={update("intended_use")} className="fc-input resize-none pt-3" placeholder="Awareness campaign, educational video, sermon, etc." data-testid="licensing-use" />
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="fc-label block mb-2">Platforms</label>
                <input value={form.platforms} onChange={update("platforms")} className="fc-input" placeholder="IG, YT, Web" data-testid="licensing-platforms" />
              </div>
              <div>
                <label className="fc-label block mb-2">Paid or Organic</label>
                <select value={form.paid_or_organic} onChange={update("paid_or_organic")} className="fc-input bg-transparent" data-testid="licensing-paid">
                  <option className="bg-an-bg">Organic</option>
                  <option className="bg-an-bg">Paid</option>
                  <option className="bg-an-bg">Mixed</option>
                </select>
              </div>
              <div>
                <label className="fc-label block mb-2">Duration</label>
                <input value={form.duration} onChange={update("duration")} className="fc-input" placeholder="e.g. 3 months" data-testid="licensing-duration" />
              </div>
            </div>
            <div>
              <label className="fc-label block mb-2">Additional Notes</label>
              <textarea rows={3} value={form.notes} onChange={update("notes")} className="fc-input resize-none pt-3" data-testid="licensing-notes" />
            </div>
            <button className="btn-primary" disabled={busy} data-testid="licensing-submit">
              {busy ? "Submitting…" : "Submit Licensing Request"}
            </button>
            {status && <div className="overline pt-2 text-an-gold" data-testid="licensing-status">{status}</div>}
          </form>
        </div>
      </section>
    </div>
  );
}
